import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { deckService } from '../services/deckService';
import { Deck, CreateDeckDto, DeckWithCreator } from '../types';
import { t } from '../utils/translations';
import { isUpmindyAndroidWebView } from '../utils/deviceDetection';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import UpgradeOptionsModal from '../components/UpgradeOptionsModal';
import QuickStudyModal from '../components/QuickStudyModal';
import DeckCreationModal from '../components/DeckCreationModal';
import ModuleIntroModal from '../components/ModuleIntroModal';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  TagIcon,
  TrashIcon,
  PencilIcon,
  PlayIcon,
  UserIcon,
  BoltIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

const Decks: React.FC = () => {
  const location = useLocation();
  const { subscription, isUpgradeRequired, checkLimit, refreshSubscription } = useSubscription();
  const { loading: authLoading } = useAuth();
  const isAndroidWebView = isUpmindyAndroidWebView();
  const [decks, setDecks] = useState<DeckWithCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeckModal, setShowDeckModal] = useState(false);
  const [editingDeck, setEditingDeck] = useState<DeckWithCreator | null>(null);
  const [deletingDeckId, setDeletingDeckId] = useState<string | null>(null);
  const [showCreatorInfo, setShowCreatorInfo] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptType, setUpgradePromptType] = useState<'deck' | 'quickstudy'>('deck');
  const [showQuickStudyModal, setShowQuickStudyModal] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);
  
  const [deckForm, setDeckForm] = useState<CreateDeckDto>({
    name: '',
    description: '',
    tags: []
  });

  // Check if we should show the intro modal on first visit
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('decks_intro_seen');
    if (!hasSeenIntro) {
      setShowIntroModal(true);
    }
  }, []);

  const handleCloseIntroModal = () => {
    localStorage.setItem('decks_intro_seen', 'true');
    setShowIntroModal(false);
  };

  // Check if we should open the create form based on navigation state
  useEffect(() => {
    const state = location.state as { showCreateForm?: boolean } | null;
    if (state?.showCreateForm) {
      setShowDeckModal(true);
      // Clear the state to prevent reopening on subsequent visits
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    // Only load decks after authentication is complete
    if (!authLoading) {
      loadDecks();
    }
  }, [showCreatorInfo, authLoading]);

  const loadDecks = async () => {
    try {
      setLoading(true);
      const decksData = showCreatorInfo 
        ? await deckService.getDecksWithCreator()
        : await deckService.getDecks().then(decks => 
            decks.map(deck => ({
              ...deck,
              userId: { _id: deck.userId, name: 'Unknown', email: 'unknown@example.com' }
            }))
          );
      setDecks(decksData);
    } catch (error) {
      console.error('Error loading decks:', error);
      toast.error(t('Failed to load decks'));
    } finally {
      setLoading(false);
    }
  };

  const loadUserDecks = async () => {
    try {
      const decksData = showCreatorInfo 
        ? await deckService.getDecksWithCreator()
        : await deckService.getDecks().then(decks => 
            decks.map(deck => ({
              ...deck,
              userId: { _id: deck.userId, name: 'Unknown', email: 'unknown@example.com' }
            }))
          );
      setDecks(decksData);
    } catch (error) {
      console.error('Error loading user decks:', error);
      toast.error(t('Failed to load decks'));
    }
  };

  const handleCreateDeck = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!deckForm.name.trim()) {
      toast.error(t('Deck name is required'));
      return;
    }

    // Check if user can create more decks based on actual deck count
    if (subscription && subscription.subscriptionTier === 'free' && decks.length >= 3) {
      setUpgradePromptType('deck');
      setShowUpgradePrompt(true);
      return;
    }

    try {
      const newDeck = await deckService.createDeck(deckForm);
      // Convert to DeckWithCreator format
      const deckWithCreator: DeckWithCreator = {
        ...newDeck,
        userId: { _id: newDeck.userId, name: 'You', email: 'current@user.com' }
      };
      setDecks([...decks, deckWithCreator]);
      setDeckForm({ name: '', description: '', tags: [] });
      setShowDeckModal(false);
      toast.success(t('Deck created successfully'));
      // Refresh subscription to update deck count
      await refreshSubscription();
      // Reload to get proper creator info
      loadDecks();
    } catch (error: any) {
      console.error('Error creating deck:', error);
      if (error.response?.data?.code === 'DECK_LIMIT_REACHED') {
        setUpgradePromptType('deck');
        setShowUpgradePrompt(true);
      } else {
        toast.error(t('Failed to create deck'));
      }
    }
  };

  const handleUpdateDeck = async (deckData: CreateDeckDto) => {
    if (!editingDeck || !deckData.name.trim()) {
      toast.error(t('Deck name is required'));
      return;
    }

    try {
      const updatedDeck = await deckService.updateDeck(editingDeck._id, deckData);
      // Convert to DeckWithCreator format
      const deckWithCreator: DeckWithCreator = {
        ...updatedDeck,
        userId: editingDeck.userId // Keep the existing creator info
      };
      setDecks(decks.map(deck => deck._id === editingDeck._id ? deckWithCreator : deck));
      setDeckForm({ name: '', description: '', tags: [] });
      setEditingDeck(null);
      setShowDeckModal(false);
      toast.success(t('Deck updated successfully'));
    } catch (error) {
      console.error('Error updating deck:', error);
      toast.error(t('Failed to update deck'));
      throw error;
    }
  };

  const handleDeleteDeck = async (deckId: string) => {
    try {
      await deckService.deleteDeck(deckId);
      setDecks(decks.filter(deck => deck._id !== deckId));
      setDeletingDeckId(null);
      toast.success(t('messages.flashcardDeleted'));
      // Refresh subscription to update deck count
      await refreshSubscription();
    } catch (error) {
      console.error('Error deleting deck:', error);
      toast.error(t('messages.failedToDelete'));
    }
  };

  const startEditing = (deck: DeckWithCreator) => {
    setEditingDeck(deck);
    setDeckForm({
      name: deck.name,
      description: deck.description || '',
      tags: deck.tags || []
    });
    setShowDeckModal(true);
  };

  const cancelModal = () => {
    setShowDeckModal(false);
    setEditingDeck(null);
    setDeckForm({ name: '', description: '', tags: [] });
  };

  const handleCreateManual = async (deckData: CreateDeckDto) => {
    if (!deckData.name.trim()) {
      toast.error(t('Deck name is required'));
      return;
    }

    // Check if user can create more decks based on actual deck count
    if (subscription && subscription.subscriptionTier === 'free' && decks.length >= 3) {
      setUpgradePromptType('deck');
      setShowUpgradePrompt(true);
      return;
    }

    try {
      const newDeck = await deckService.createDeck(deckData);
      // Convert to DeckWithCreator format
      const deckWithCreator: DeckWithCreator = {
        ...newDeck,
        userId: { _id: newDeck.userId, name: 'You', email: 'current@user.com' }
      };
      setDecks([...decks, deckWithCreator]);
      setDeckForm({ name: '', description: '', tags: [] });
      toast.success(t('Deck created successfully'));
      // Refresh subscription to update deck count
      await refreshSubscription();
      // Reload to get proper creator info
      loadDecks();
    } catch (error: any) {
      console.error('Error creating deck:', error);
      if (error.response?.data?.code === 'DECK_LIMIT_REACHED') {
        setUpgradePromptType('deck');
        setShowUpgradePrompt(true);
      } else {
        toast.error(t('Failed to create deck'));
      }
      throw error;
    }
  };

  const handleCreateWithAI = async (aiRequest: {
    topic: string;
    description: string;
    cardCount: number;
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    language: 'portuguese' | 'english' | 'spanish';
    additionalInfo?: string;
  }) => {
    try {
      const response = await deckService.requestAIDeck(aiRequest);
      // Refresh the deck list to show the newly created deck
      await loadDecks();
      // Refresh subscription to update deck count
      await refreshSubscription();
      return response;
    } catch (error) {
      console.error('Error requesting AI deck:', error);
      throw error;
    }
  };

  const getCurrentDecks = () => {
    // Always show user's own decks since we removed shared decks
    return decks;
  };

  const filteredDecks = getCurrentDecks().filter(deck =>
    deck.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deck.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deck.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading || authLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50 dark:from-gray-900 dark:via-blue-900/10 dark:to-indigo-900/20 transition-all duration-500">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600 dark:from-blue-400 dark:via-blue-300 dark:to-indigo-400 bg-clip-text text-transparent">
            Flashcards
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl">
            Organize seus flashcards em decks para aprendizado focado
          </p>
          {/* Quiz limit info for free users - Hidden in Android WebView */}
          {subscription && subscription.subscriptionTier === 'free' && !isAndroidWebView && (
            <div className="flex items-center space-x-2 text-sm">
              <span className={`font-medium ${decks.length >= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                {decks.length}/3 decks no plano gratuito
              </span>
              <Link 
                to="/pricing"
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold underline transition-colors duration-200"
              >
                Upgrade
              </Link>
            </div>
          )}
        </div>
        <div className="mt-6 sm:mt-0 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Quick Study Button - Show when user has decks and is premium (or not Android WebView for free users) */}
            {decks.length > 0 && (subscription?.subscriptionTier === 'premium' || !isAndroidWebView) && (
              <button
                onClick={() => {
                  if (subscription?.subscriptionTier === 'premium') {
                    setShowQuickStudyModal(true);
                  } else {
                    setUpgradePromptType('quickstudy');
                    setShowUpgradePrompt(true);
                  }
                }}
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600 hover:from-emerald-600 hover:via-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200 backdrop-blur-sm shadow-emerald-500/25 hover:shadow-emerald-600/30"
              >
                <BoltIcon className="h-5 w-5 mr-2" />
                Estudo Rápido
              </button>
            )}

            {/* Create Deck Button */}
            <button
              onClick={() => {
                // Check if user can create more decks based on actual deck count
                if (subscription && subscription.subscriptionTier === 'free' && decks.length >= 3) {
                  if (isAndroidWebView) {
                    // Show toast notification instead of modal
                    toast.error('Faça upgrade para criar mais do que 3 flashcards.', {
                      duration: 4000,
                      position: 'top-center',
                      style: {
                        background: '#EF4444',
                        color: '#fff',
                        fontWeight: 'bold',
                      },
                    });
                    return;
                  } else {
                    setUpgradePromptType('deck');
                    setShowUpgradePrompt(true);
                    return;
                  }
                }
                setShowDeckModal(true);
              }}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-indigo-500 via-blue-600 to-indigo-600 hover:from-indigo-600 hover:via-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200 backdrop-blur-sm shadow-indigo-500/25 hover:shadow-indigo-600/30"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Criar Flashcards
            </button>
          </div>
        </div>
        </div>
        {/* Search - Only show if there are at least 3 decks */}
        {decks.length >= 3 && (
          <div className="relative mt-8 mb-6">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-200/50 dark:border-gray-700/50 rounded-2xl leading-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-300 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 dark:focus:ring-indigo-400/50 dark:focus:border-indigo-400/50 transition-all duration-300 shadow-lg dark:shadow-xl hover:shadow-xl dark:hover:shadow-2xl hover:bg-white dark:hover:bg-gray-800 text-lg"
            />
          </div>
        )}

        {/* View Options - Only show when user has decks */}
        {decks.length > 0 && (
          <div className="flex items-center space-x-4 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 p-4 rounded-xl shadow-lg mb-6">
            <label className="flex items-center cursor-pointer group">
              <input
                type="checkbox"
                checked={showCreatorInfo}
                onChange={(e) => setShowCreatorInfo(e.target.checked)}
                className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:checked:bg-indigo-500 transition-all duration-200"
              />
              <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors duration-200">Mostrar criado por</span>
            </label>
          </div>
        )}

        {/* Tabs - Only show when user has decks */}        {/* Message for users without decks viewing shared decks */}
        {decks.length === 0 && filteredDecks.length > 0 && (
          <div className="mt-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 border border-blue-200/50 dark:border-blue-700/50 rounded-2xl p-8 mb-8 shadow-xl backdrop-blur-sm">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="h-16 w-16 bg-gradient-to-br from-blue-500 to-indigo-600 dark:from-blue-400 dark:to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-900 to-indigo-900 dark:from-blue-100 dark:to-indigo-100 bg-clip-text text-transparent mb-6">
                Copie flashcards já prontos ou crie o seu do zero
              </h3>
              <p className="text-lg text-blue-700 dark:text-blue-200 mb-8 max-w-2xl mx-auto leading-relaxed">
                Explore os flashcards compartilhados abaixo e copie os que mais gostou, ou comece criando o seu próprio deck personalizado.
              </p>
              <button
                onClick={() => {
                  // Check if user can create more decks based on actual deck count
                  if (subscription && subscription.subscriptionTier === 'free' && decks.length >= 3) {
                    setUpgradePromptType('deck');
                    setShowUpgradePrompt(true);
                    return;
                  }
                  setShowDeckModal(true);
                }}
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-semibold rounded-xl text-white bg-gradient-to-r from-indigo-500 via-blue-600 to-indigo-600 hover:from-indigo-600 hover:via-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-blue-900 shadow-lg transition-all duration-200 hover:scale-105"
              >
                <svg className="h-6 w-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Criar Meu Primeiro Flashcard
              </button>
            </div>
          </div>
        )}

      {/* Decks Grid */}
      {filteredDecks.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-lg border border-gray-200 dark:border-gray-700">
          <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            Você ainda não tem flashcards.
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Crie seu primeiro flashcard para começar a estudar!
          </p>
          {!searchTerm && (
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => {
                  // Check if user can create more decks based on actual deck count
                  if (subscription && subscription.subscriptionTier === 'free' && decks.length >= 3) {
                    setUpgradePromptType('deck');
                    setShowUpgradePrompt(true);
                    return;
                  }
                  setShowDeckModal(true);
                }}
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-indigo-500 via-blue-600 to-indigo-600 hover:from-indigo-600 hover:via-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200 backdrop-blur-sm shadow-indigo-500/25 hover:shadow-indigo-600/30"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('decks.createFirstDeck')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 mt-8">
          {filteredDecks.map((deck) => (
            <div
              key={deck._id}
              className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-xl rounded-2xl hover:shadow-2xl dark:hover:shadow-2xl dark:hover:shadow-gray-900/50 transition-all duration-300 hover:-translate-y-2 hover:scale-105 hover:border-indigo-300/50 dark:hover:border-indigo-500/50"
            >
              {/* Card Header with gradient overlay */}
              <div className="relative bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 dark:from-gray-800 dark:via-blue-900/20 dark:to-purple-900/20 p-6 border-b border-gray-100/50 dark:border-gray-700/50">
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-indigo-500/5 to-purple-500/10 dark:from-transparent dark:via-indigo-400/10 dark:to-purple-400/10"></div>
                <div className="relative flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                    {deck.name}
                  </h3>
                </div>
                
                {deck.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed">
                    {deck.description}
                  </p>
                )}
              </div>

              {/* Card Body */}
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 space-x-6 mb-4">
                  <div className="flex items-center bg-blue-50 dark:bg-blue-900/30 px-3 py-2 rounded-lg">
                    <BookOpenIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                    <span className="font-medium">{deck.cardCount} {t('cards')}</span>
                  </div>
                </div>

                {showCreatorInfo && decks.length > 0 ? (
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-2 rounded-lg">
                    <UserIcon className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                    <span className="font-medium">
                      Created by: {deck.userId.name}
                    </span>
                  </div>
                ) : null}
                
                <div className="flex flex-row gap-3">
                  <>
                    {deck.cardCount > 0 && (
                      <Link
                        to={`/study/${deck._id}`}
                        className="flex-1 inline-flex justify-center items-center px-4 py-3 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600 hover:from-emerald-600 hover:via-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 dark:focus:ring-offset-gray-800 transition-all duration-200 hover:scale-105 shadow-lg shadow-emerald-500/25"
                      >
                        <PlayIcon className="h-4 w-4 mr-2" />
                        {t('Estudar')}
                      </Link>
                    )}
                    <Link
                      to={`/decks/management/${deck._id}`}
                      className={`inline-flex justify-center items-center px-4 py-3 border shadow-lg text-sm font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 hover:scale-105 ${
                        deck.cardCount === 0 
                          ? 'flex-1 border-transparent text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 focus:ring-indigo-500 shadow-indigo-500/25'
                          : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-indigo-500 shadow-gray-500/25'
                      }`}
                    >
                      {deck.cardCount === 0 ? (
                        <>
                          <PencilIcon className="h-4 w-4 mr-2" />
                          {t('cards.createCards')}
                        </>
                      ) : (
                        <>
                          <Cog6ToothIcon className="h-4 w-4 mr-2" />
                          {t('decks.manage')}
                        </>
                      )}
                    </Link>
                  </>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingDeckId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-96 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4">
                {t('decks.deleteConfirmTitle')}
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('decks.deleteConfirmMessage')}
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setDeletingDeckId(null)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={() => handleDeleteDeck(deletingDeckId)}
                  className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                >
                  {t('Delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        upgradePromptType === 'quickstudy' ? (
          <UpgradeOptionsModal
            title="Recurso Premium"
            message="O Estudo Rápido é um recurso premium que permite estudar flashcards de múltiplos decks de forma inteligente."
            icon="info"
            premiumBenefits={[
              'Decks e cards ilimitados',
              'Estudo Rápido em múltiplos decks',
              'Recursos avançados de estudo',
              'Análises detalhadas',
              'Funcionalidade de exportar/importar'
            ]}
            onClose={() => setShowUpgradePrompt(false)}
          />
        ) : (
          <UpgradeOptionsModal
            message="Você atingiu o limite de 3 decks no plano gratuito."
            feature="decks ilimitados"
            onClose={() => setShowUpgradePrompt(false)}
          />
        )
      )}

      {/* Quick Study Modal */}
      <QuickStudyModal
        isOpen={showQuickStudyModal}
        onClose={() => setShowQuickStudyModal(false)}
      />

      {/* Module Intro Modal */}
      <ModuleIntroModal
        isOpen={showIntroModal}
        onClose={handleCloseIntroModal}
        moduleType="decks"
      />

      {/* Deck Creation Modal */}
      <DeckCreationModal
        isOpen={showDeckModal}
        onClose={cancelModal}
        onCreateManual={editingDeck ? handleUpdateDeck : handleCreateManual}
        onCreateWithAI={handleCreateWithAI}
        editingDeck={editingDeck}
        deckForm={deckForm}
        setDeckForm={setDeckForm}
        subscription={subscription}
        onUpgradePrompt={() => {
          setUpgradePromptType('deck');
          setShowUpgradePrompt(true);
        }}
      />
      </div>
    </div>
  );
};

export default Decks;
